import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAdminUsers } from '../../hooks/useAdminUsers';

const SubscriptionFeatureConfig = () => {
  const { 
    featureConfig, 
    loading, 
    error,
    loadFeatureConfig,
    handleUpdateFeatureConfig
  } = useSubscription();
  
  const {
    users: adminUsers,
    usersLoading: adminLoading,
    usersError: adminError,
    loadUsers: loadAdminUsers
  } = useAdminUsers();
  
  const [config, setConfig] = useState({

    enforcement: 'OFF',

    testerUserIds: [],
    testerEmails: []
  });
  
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newTesterEmail, setNewTesterEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadFeatureConfig();
    loadAdminUsers();
  }, [loadFeatureConfig, loadAdminUsers]);

  useEffect(() => {
    if (featureConfig) {
      setConfig(featureConfig);
    }
  }, [featureConfig]);

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await handleUpdateFeatureConfig(config);
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const addTesterId = () => {
    if (selectedUserId && !config.testerUserIds.includes(parseInt(selectedUserId))) {
      setConfig(prev => ({
        ...prev,
        testerUserIds: [...prev.testerUserIds, parseInt(selectedUserId)]
      }));
      setSelectedUserId('');
    }
  };

  const removeTesterId = (id) => {
    setConfig(prev => ({
      ...prev,
      testerUserIds: prev.testerUserIds.filter(testId => testId !== id)
    }));
  };

  const addTesterEmail = () => {
    if (newTesterEmail && !config.testerEmails.includes(newTesterEmail)) {
      setConfig(prev => ({
        ...prev,
        testerEmails: [...prev.testerEmails, newTesterEmail]
      }));
      setNewTesterEmail('');
    }
  };

  const removeTesterEmail = (email) => {
    setConfig(prev => ({
      ...prev,
      testerEmails: prev.testerEmails.filter(testEmail => testEmail !== email)
    }));
  };

  // Get user display name for a given user ID
  const getUserDisplayName = (userId) => {
    if (!adminUsers || !Array.isArray(adminUsers)) {
      return `User ${userId}`;
    }
    const user = adminUsers.find(u => u.id === userId);
    return user ? `${user.username} (${user.email})` : `User ${userId}`;
  };

  if (loading) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          Subscription Feature Configuration
        </h1>
        <div className="text-center text-lg text-gray-600 p-8">Loading configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
          Subscription Feature Configuration
        </h1>
        <div className="text-center text-lg text-red-600 p-8">Error loading configuration: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-4xl font-bold text-center text-gray-800 mb-8 md:text-5xl">
        Subscription Feature Configuration
      </h1>
      
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-teal-500">
          General Settings
        </h2>
        
        <div className="space-y-6">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-800 text-base flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
              Enable Subscription Features
            </label>
            <p className="text-sm text-gray-600 ml-7">
              When enabled, subscription features will be available to users
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-800 text-base">Enforcement Mode:</label>
            <select
              value={config.enforcement}
              onChange={(e) => handleInputChange('enforcement', e.target.value)}
              disabled={!config.enabled}
              className="w-full p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="OFF">Off</option>
              <option value="DRY_RUN">Dry run</option>
              <option value="ADMINS_ONLY">Admins only</option>
              <option value="ALL">All users</option>
            </select>
            <p className="text-sm text-gray-600">
              How strictly to enforce subscription requirements
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-800 text-base flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.credits}
                onChange={(e) => handleInputChange('credits', e.target.checked)}
                disabled={!config.enabled}
                className="w-5 h-5 cursor-pointer"
              />
              Enable Credit System
            </label>
            <p className="text-sm text-gray-600 ml-7">
              Allow users to purchase and use credits for premium features
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 pb-2 border-b-2 border-teal-500">
          Test Users
        </h2>
        
        <div className="space-y-6">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-800 text-base">Add Tester User:</label>
            <div className="flex gap-2">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={!config.enabled || adminLoading}
                className="flex-1 p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Select a user from the platform</option>
                {adminUsers && adminUsers.length > 0 ? (
                  adminUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    {adminLoading ? 'Loading users...' : 'No users available'}
                  </option>
                )}
              </select>
              <button 
                onClick={addTesterId} 
                disabled={!selectedUserId || !config.enabled}
                className="px-6 py-3 bg-green-600 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Users that will have access to subscription features for testing
            </p>
          </div>

          {config.testerUserIds.length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-base">Current Tester Users:</label>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {config.testerUserIds.map(id => (
                    <div key={id} className="flex items-center gap-2 bg-teal-600 text-white px-3 py-2 rounded-full text-sm">
                      {getUserDisplayName(id)}
                      <button 
                        onClick={() => removeTesterId(id)}
                        className="text-white hover:text-red-200 font-bold text-lg leading-none w-5 h-5 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-800 text-base">Add Tester Email:</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={newTesterEmail}
                onChange={(e) => setNewTesterEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={!config.enabled}
                className="flex-1 p-3 border border-gray-300 rounded-lg text-base bg-white text-gray-800 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200 disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <button 
                onClick={addTesterEmail} 
                disabled={!newTesterEmail || !config.enabled}
                className="px-6 py-3 bg-green-600 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Email addresses that will have access to subscription features for testing
            </p>
          </div>

          {config.testerEmails.length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-gray-800 text-base">Current Tester Emails:</label>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {config.testerEmails.map(email => (
                    <div key={email} className="flex items-center gap-2 bg-teal-600 text-white px-3 py-2 rounded-full text-sm">
                      {email}
                      <button 
                        onClick={() => removeTesterEmail(email)}
                        className="text-white hover:text-red-200 font-bold text-lg leading-none w-5 h-5 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center mb-8">
        <button
          onClick={handleSave}
          disabled={isSaving || adminLoading}
          className="px-8 py-4 bg-teal-600 text-white border-none rounded-lg text-lg font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-teal-700 hover:enabled:transform hover:enabled:-translate-y-1 hover:enabled:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {adminError && (
        <div className="bg-red-600 text-white rounded-xl p-6 mt-4">
          <h3 className="text-xl font-semibold mb-2">Configuration Error</h3>
          <p className="text-base opacity-90">{adminError}</p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionFeatureConfig;
